import React from "react";
import Title from "../../ui/Title";

const ClimateImpact = () => {
  return (
    <div>
      <Title title="Climate change impacting the Nooraverse" className="mb-6" />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          In 2024, India faced its{" "}
          <a
            href="https://www.dw.com/en/india-heat-and-sickness-march-together-as-climate-shifts/a-69796336"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            worst heatwave
          </a>{" "}
          in over a decade, resulting in hundreds of deaths and adversely
          affecting the health of thousands more. Temperatures shot past 50
          degrees celsius (122 degrees Fahrenheit) in several places, including
          New Delhi and the northern states of Rajasthan and Uttar Pradesh.
        </p>

        <p>
          Caregivers we work with, mostly from low-income groups and
          communities, were severely affected by this, further increasing their
          care burden. They continue to experience both direct and indirect
          impacts from extreme heat. Our program associates, who are mostly
          field workers, have also been affected by this. In response, Noora
          Health has provided heatwave survival kits to all field staff. The
          organisation also created messaging on hydration and heat safety for
          the Care Companion Program (CCP) and its employees. Following the
          heatwave, flooding occurred in several regions we work in,
          highlighting that the severity of global warming and climate change is
          only set to increase. Our mission of supporting caregivers and
          caregiving must focus on this crisis, as it directly and indirectly
          affects our work and the impact of it. We need to be more mindful as
          the crisis deepens in the years ahead and include strategies to ensure
          preparedness, as well as support caregivers and healthcare workers who
          are impacted by climate extremes.
        </p>

        <div id="india" className="mb-8 scroll-mt-24 space-y-4">
          <h3 className="text-xl font-bold text-black mb-4 mt-8">India</h3>

          <p>
            India has diverse ecosystems, including mountainous, desert, plains,
            and coastal terrain, and is therefore susceptible to a wide range of
            climate change effects, including rising temperatures leading to
            drought risks and heavy rainfall events leading to river and coastal
            flooding. According to{" "}
            <a
              href="https://climateknowledgeportal.worldbank.org/sites/default/files/country-profiles/15503-WB_India%20Country%20Profile-WEB.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              a World Bank country climate risk profile
            </a>
            , India is already seeing heat-related health issues including
            diarrhea and malnourishment, and marginalized communities,
            especially women, are likely to bear the brunt of these effects. The
            country stands to lose 5.21 percent of its GDP by 2050, according to
            the{" "}
            <a
              href="https://www.g20climaterisks.org/india/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              G20 Climate Risk Atlas
            </a>
            .
          </p>

          <p>
            The Government of India has had an action plan on climate change
            since 2008, and, in 2009, directed all of its states to create
            sub-national climate action plans. However,{" "}
            <a
              href="https://www.tandfonline.com/doi/abs/10.1080/14693062.2021.1953434"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              an analysis published in 2021
            </a>{" "}
            found that most state policies ‘explicitly mention gender as a
            mediator of vulnerability and adaptive capacity but operationalize
            it inadequately and unevenly.’ In 2018, India{" "}
            <a
              href="https://ncdc.mohfw.gov.in/wp-content/uploads/2024/07/NATIONAL-ACTION-PLAN-FOR-CLIMATE-CHANGE-HUMAN-HEALTH-2021.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              published
            </a>{" "}
            its National Action Plan for Climate Change and Human Health, which
            aims to raise population-level awareness on adaptation to and
            prevention of climate-sensitive illnesses; train; create
            climate-resistant healthcare services and infrastructure; and
            integrate monitoring systems of health-related data with
            meteorological, environmental, socio-economic and occupational data.
            While the plan mentions women among those at-risk of
            climate-sensitive illnesses, it does not mention reasons for the
            increased vulnerabilities or impact of climate change on women’s
            health.
          </p>

          <p>
            India’s{" "}
            <a
              href="https://www4.unfccc.int/sites/SubmissionsStaging/Documents/201804051550---India%20Gender%20submission.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              response to the United Nations
            </a>{" "}
            on developing a climate change and gender action plan acknowledges
            that women are at greater risk of climate impacts than men, ‘as
            their needs are different and competitive’. The response document
            highlights the need for gender-sensitive interventions, such as
            providing clean fuel to women and encouraging their participation in
            decision-making.
          </p>
        </div>

        <div id="bangladesh" className="mb-8 scroll-mt-24 space-y-4">
          <h3 className="text-xl font-bold text-black mb-4 mt-8">Bangladesh</h3>

          <p>
            According to the World Bank’s{" "}
            <a
              href="https://climateknowledgeportal.worldbank.org/sites/default/files/country-profiles/16813-WB_Bangladesh%20Country%20Profile-WEB.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Climate Risk Country Profile
            </a>
            , Bangladesh is one of the countries at the highest risk globally
            from climate-related hazards such as coastal flooding, sea-level
            rise, and ‘extreme precipitation events’. The profile cites research
            estimating that over half the population, that is, 90 million
            people, live in areas categorized as “high climate exposure areas”,
            which are affected by multiple climate hazards. It also estimates
            that up to one-third of Bangladesh’s agricultural GDP could be lost
            to climate variability and extreme events by 2050.
          </p>

          <p>
            According to an Asian Development Bank{" "}
            <a
              href="https://www.adb.org/sites/default/files/linked-documents/56253-001-ssa.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              sector assessment,
            </a>{" "}
            damages from riverine flooding and decreased agricultural and worker
            productivity, caused by temperature increase, could result in losses
            of 2 percent of GDP by 2050.
          </p>

          <p>
            Bangladesh first put in place a Climate Change and Gender Action
            Plan (CGAP) in 2013, which was{" "}
            <a
              href="https://moef.portal.gov.bd/sites/default/files/files/moef.portal.gov.bd/page/ac0ce881_4b1d_4844_a426_1b6ee36d2453/ccGAP%20Final%20Print%20Version-25%20April%202024c.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              updated in 2023
            </a>{" "}
            by UN Women in partnership with the Bangladesh Climate Change Trust
            (BCCT), keeping the existing climate policies of the country in
            mind. The policy acknowledges that women are more vulnerable to
            climate change, partly due to their responsibilities of ‘production
            and provision of food, guarding family health, caregiving, etc.,’
            and because of their difference in access to resources and mobility.
            The plan’s suggested strategies do not explicitly mention
            caregivers, but categorize suggestions for establishing and
            improving gender-responsive systems in livelihood, natural resource
            management, infrastructure, leadership, capacity building, and
            communication, that center around increasing access, knowledge, and
            improving women’s participation in decision-making.
          </p>
        </div>

        <div id="indonesia" className="mb-8 scroll-mt-24 space-y-4">
          <h3 className="text-xl font-bold text-black mb-4 mt-8">Indonesia</h3>

          <p>
            Indonesia is vulnerable to shifts in rainfall patterns and
            increasing temperature resulting in floods, droughts, and long-term
            changes from sea-level rise. According to a World Bank{" "}
            <a
              href="https://climateknowledgeportal.worldbank.org/country/indonesia"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              country summary
            </a>
            , Indonesia has a high population density in hazard-prone areas.
            This, along with a dependence on the country’s natural resources,
            makes it especially vulnerable to climate variability. According to
            a{" "}
            <a
              href="https://www.adb.org/sites/default/files/publication/700411/climate-risk-country-profile-indonesia.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              2019 World Bank Climate Profile
            </a>
            , over 110 million people in approximately 60 Indonesian cities are
            exposed to natural disasters, including tornadoes, flooding and
            landslides. The{" "}
            <a
              href="https://www.g20climaterisks.org/indonesia/#:~:text=Economic%20Impacts&text=Indonesia%20faces%20multiple%20threats%20from,rises%20to%2013.27%25%20by%202100."
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              G20 Climate Risk Atlas
            </a>{" "}
            also points out that climate impacts could cost Indonesia 4.4
            percent of its GDP by 2050, with the poorest bearing the brunt of
            this burden.
          </p>

          <p>
            The Government of Indonesia has made{" "}
            <a
              href="https://www4.unfccc.int/sites/SubmissionsStaging/Documents/202204151130---Indonesia%20Submission%20on%20Gender%20Differentiated%20Impact%202022_FINAL.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              submissions
            </a>{" "}
            to the United Nations acknowledging that women are
            disproportionately impacted by climate change than men due to their
            triple gender roles (reproductive, productive, and community
            managing), and pre-existing social and cultural norms. The
            submission also states that women are not only responsible for
            caregiving of children, but are often expected to take on emergency
            responsibilities such as evacuation during climate-related
            disasters. Another{" "}
            <a
              href="https://genderclimatetracker.org/sites/default/files/Resources/202204151129---Indonesia%20Submission%20on%20Gender%20Action%20Plan%202022_FINAL.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              submission
            </a>{" "}
            lists the Government of Indonesia’s efforts to ensure gender equity
            in climate related efforts.
          </p>

          <p>
            According to a news report in 2024, the Government of Indonesia has{" "}
            <a
              href="https://en.antaranews.com/news/309492/govt-launches-gender-and-climate-change-action-plan-for-social-justice"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              launched
            </a>{" "}
            its Gender and Climate Change Action Plan. However, the plan in its
            entirety is not currently available to the public.
          </p>
        </div>

        <div id="nepal" className="mb-8 scroll-mt-24 space-y-4">
          <h3 className="text-xl font-bold text-black mb-4 mt-8">Nepal</h3>

          <div>
            <p>
              According to a{" "}
              <a
                href="https://iris.who.int/bitstream/handle/10665/329615/seajph2017v6n2p9.pdf?sequence=1&isAllowed=y"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                2017 perspective
              </a>{" "}
              published by the World Health Organization (WHO), Nepal is at risk
              of the increasing frequency and severity of extreme weather
              events, such as heavy rainfall, droughts, heat waves and cold
              waves. The article states that due to increased temperature,
              diminished snowfall, and rapid retreat of glaciers, there is a
              scarcity of water available for drinking, livestock, and
              irrigation in the hill and mountain regions of Nepal. There is
              also an increase in vector-borne and water-borne diseases. The
              perspective further highlights how climate impacts could affect
              health outcomes for women. Limited access to water could affect
              their workload, for instance, as women bear the responsibility of
              taking care of ill family members, children, and the elderly.
              Additionally, the article states that climate change could
              increase the migration of men overseas for work, causing women,
              especially in rural areas, to deal with the health impacts of
              climate change. Over 80 percent of the country’s population is
              exposed to the risk of natural hazards, which include earthquakes,
              droughts, floods, landslides, extreme temperature, and glacier
              lake outburst floods, according to a{" "}
              <a
                href="https://reliefweb.int/report/nepal/disaster-risk-reduction-nepal-status-report-july-2019"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                2019 report.
              </a>{" "}
            </p>

            <p>
              According to studies by the Asian Development Bank cited{" "}
              <a
                href="https://climateknowledgeportal.worldbank.org/country/nepal"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline"
              >
                by the World Bank
              </a>
              , Nepal could lose 2.2 percent of its annual GDP due to climate
              change by 2050
            </p>
          </div>

          <p>
            Nepal has had a{" "}
            <a
              href="https://portals.iucn.org/union/sites/union/files/doc/nepal.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              Climate Change and Gender Action Plan (CGAP)
            </a>{" "}
            in place since 2012, which mentions the need to involve women in
            decision-making, as climate change exacerbates their workload, and
            affects their ability to earn and get an education. The Government
            of Nepal’s{" "}
            <a
              href="https://unfccc.int/sites/default/files/resource/NAP_Nepal_2021.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              National Adaptation Plan
            </a>{" "}
            for 2021-2025 also acknowledged the differential impact of climate
            change on men and women, especially for women in agriculture, and
            suggested measures to increase their access to resources, healthcare
            infrastructure, knowledge and forums for decision-making.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClimateImpact;
